// CuentaFormXL - Sprint 44 Fase 3 Vista 8.
// Form de cuenta contable. Carga catalogo de padres en background.
// XLInput + XLSelect + XLCheck. msg auto-dismiss 4s.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import * as svc from '../services/contabilidadService';
import XLInput from '../../../components/xl/XLInput';
import XLSelect from '../../../components/xl/XLSelect';
import XLCheck from '../../../components/xl/XLCheck';
import XLSkeleton from '../../../components/xl/XLSkeleton';
import { TIPO_OPTIONS_FORM } from '../utils';

const CSS_ID = 'xl-cuenta-form-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-hdr { display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.xl-hdr__left { display: flex; align-items: center; gap: 14px; min-width: 0 }
.xl-hdr__back {
  font: 500 13px/1 var(--xl-font-sans);
  color: var(--xl-color-text-secondary);
  background: transparent; border: none; cursor: pointer;
  padding: 6px 8px; border-radius: var(--xl-radius-md);
}
.xl-hdr__back:hover { background: var(--xl-color-surface-alt); color: var(--xl-color-text) }
.xl-hdr__title { margin: 0; font-size: 20px; font-weight: 600; line-height: 1.2 }
.xl-hdr__sub { margin: 2px 0 0; font-size: 12px; color: var(--xl-color-text-secondary); font-family: var(--xl-font-mono) }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
}
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse) }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }

.xl-banner { padding: 10px 14px; margin-bottom: 12px; border-radius: var(--xl-radius-md); font-size: 13px }
.xl-banner--ok  { background: rgba(5,150,105,0.10); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-banner--err { background: rgba(220,38,38,0.10); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }

.xl-card {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  padding: 20px;
}
.xl-grid {
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px;
}
@media (max-width: 720px) { .xl-grid { grid-template-columns: 1fr } }
.xl-grid__full { grid-column: 1 / -1 }
.xl-loading { display: flex; flex-direction: column; gap: 10px }
`;

const NATURALEZA_OPTIONS = [
  { value: 'debito',  label: 'Debito' },
  { value: 'credito', label: 'Credito' },
];
const MSG_AUTO_DISMISS_MS = 4000;
const EMPTY = {
  codigo: '', nombre: '', tipo: 'activo', nivel: 3,
  padre_id: null, naturaleza: 'debito', activo: true,
};

export default function CuentaFormXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'nueva';

  const [form, setForm] = useState(EMPTY);
  const [padres, setPadres] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const msgTimer = useRef(null);
  const pushMsg = useCallback((kind, text) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    setMsg({ kind, text });
    msgTimer.current = setTimeout(() => setMsg(null), MSG_AUTO_DISMISS_MS);
  }, []);
  useEffect(() => () => { if (msgTimer.current) clearTimeout(msgTimer.current); }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await svc.listCuentas({ activo: '' });
        if (alive) setPadres(list || []);
      } catch (_) { if (alive) setPadres([]); }
      if (isNew) { if (alive) { setForm(EMPTY); setLoaded(true); } return; }
      try {
        const d = await svc.getCuenta(id);
        if (alive) setForm({ ...EMPTY, ...(d || {}) });
      } catch (_) {
        if (alive) pushMsg('err', 'No se pudo cargar la cuenta');
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [id, isNew, pushMsg]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.padre_id === '' || payload.padre_id === null) payload.padre_id = null;
      else payload.padre_id = Number(payload.padre_id);
      payload.nivel = Number(payload.nivel);
      if (isNew) {
        const r = await svc.createCuenta(payload);
        pushMsg('ok', 'Cuenta creada');
        if (r && r.id) navigate('/contabilidad/cuentas/' + r.id, { replace: true });
      } else {
        const r = await svc.updateCuenta(id, payload);
        if (r) setForm((f) => ({ ...f, ...r }));
        pushMsg('ok', 'Cuenta actualizada');
      }
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div className="xl-page">
        <div className="xl-loading" aria-busy="true">
          <XLSkeleton variant="line" width="30%" height={20} />
          <XLSkeleton variant="rect" height={220} />
        </div>
      </div>
    );
  }

  const padreOptions = [
    { value: '', label: '(Sin padre - raiz)' },
    ...padres
      .filter((p) => p.id !== Number(id))
      .map((p) => ({ value: String(p.id), label: p.codigo + ' - ' + p.nombre })),
  ];

  return (
    <div className="xl-page">
      <header className="xl-hdr">
        <div className="xl-hdr__left">
          <button type="button" className="xl-hdr__back"
            onClick={() => navigate('/contabilidad/cuentas')}>{'\u2190 Volver'}</button>
          <div>
            <h2 className="xl-hdr__title">
              {isNew ? 'Nueva cuenta' : (form.nombre || 'Cuenta')}
            </h2>
            {form.codigo && <p className="xl-hdr__sub">{form.codigo}</p>}
          </div>
        </div>
        <button type="button" className="xl-btn xl-btn--primary"
          onClick={onSave} disabled={saving}>
          {saving ? 'Guardando\u2026' : (isNew ? 'Crear' : 'Guardar')}
        </button>
      </header>

      {msg && (
        <div className={'xl-banner ' + (msg.kind === 'ok' ? 'xl-banner--ok' : 'xl-banner--err')}
          role={msg.kind === 'ok' ? 'status' : 'alert'}>
          {msg.text}
        </div>
      )}

      <div className="xl-card">
        <div className="xl-grid">
          <XLInput label="Codigo *" value={form.codigo}
            onChange={(e) => set('codigo', e.target.value)} placeholder="110505" />
          <XLInput label="Nombre *" value={form.nombre}
            onChange={(e) => set('nombre', e.target.value)} placeholder="Caja general" />
          <XLSelect label="Tipo" value={form.tipo}
            onChange={(e) => set('tipo', e.target.value)} options={TIPO_OPTIONS_FORM} />
          <XLSelect label="Naturaleza" value={form.naturaleza}
            onChange={(e) => set('naturaleza', e.target.value)} options={NATURALEZA_OPTIONS} />
          <XLInput label="Nivel (1-5)" type="number" min="1" max="5"
            value={form.nivel}
            onChange={(e) => set('nivel', e.target.value)}
            hint="Nivel 1-2 = agrupador. 3+ = hoja (recibe movimientos)." />
          <XLSelect label="Cuenta padre"
            value={form.padre_id == null ? '' : String(form.padre_id)}
            onChange={(e) => set('padre_id', e.target.value || null)}
            options={padreOptions} />
          <div className="xl-grid__full">
            <XLCheck checked={!!form.activo}
              onChange={(v) => set('activo', v)} label="Cuenta activa" />
          </div>
        </div>
      </div>
    </div>
  );
}
